export var HealthGorillaResourceType;
(function (HealthGorillaResourceType) {
    /**
     * FHIR - Account
     * Derived from who will pay for the order in the order flow.
     * Account resource will be created per RequestGroup to determine
     * the payment method that'll be used to pay for the lab order.
     *
     * The Account resource acts as a central record against which charges, payments,
     * and adjustments are applied.
     * https://www.hl7.org/fhir/account.html
     */
    HealthGorillaResourceType["ACCOUNT"] = "Account";
    /**
     * FHIR - Coverage
     * CareSend - InusranceInfo
     *
     * The Coverage resource is intended to provide the high-level identifiers and descriptors
     * of an insurance plan, typically the information which would appear on an insurance card,
     * which may be used to pay, in part or in whole,
     * for the provision of health care products and services.
     * https://www.hl7.org/fhir/account.html
     */
    HealthGorillaResourceType["COVERAGE"] = "Coverage";
    /**
     * FHIR - Organization
     * A CareSend Office will be represented as an Organization of type 'Practice (code: PR)'
     * Insurances supported will be represented as an Organization of type 'Insurance Provider (code: IP)'
     *
     * A formally or informally recognized grouping of people or organizations formed for the purpose
     * of achieving some form of collective action.
     * In our case, this will be used for Insurance Providers.
     * https://www.hl7.org/fhir/organization.html
     */
    HealthGorillaResourceType["ORGANIZATION"] = "Organization";
    /**
     * FHIR - RequestGroup.
     * CareSend - Requisition Form
     *
     * Represents ProcedureRequests that will be performed on a patient.
     * The RequestGroups has a 'contains' property that'll contain the ProcedureRequest.
     * It'll also have a 'extensions' property where the ProcedureRequest, Practitioner, and Patient
     * will be 'literally referenced' within the resource.
     * https://www.hl7.org/fhir/requestgroup.html
     */
    HealthGorillaResourceType["REQUEST_GROUP"] = "RequestGroup";
    /**
     * FHIR - Patient
     * CareSend - Patient
     *
     * Information about an individual receiving care.
     * https://www.hl7.org/fhir/patient.html
     */
    HealthGorillaResourceType["PATIENT"] = "Patient";
    /**
     * FHIR - Practitioner
     * CareSend - Prescriber
     *
     * A person with a formal responsibility in the provisioning of healthcare or related services
     * https://hl7.org/fhir/R4/practitioner.html
     */
    HealthGorillaResourceType["PRACTITIONER"] = "Practitioner";
    /**
     * FHIR - PractitionerRole
     * No associated CareSend data type
     *
     * Defines the roles a Practitioner has within an organization
     * Used for creating practitioners within the Health Gorilla system
     * https://hl7.org/fhir/R4/practitionerrole.html#bnc
     */
    HealthGorillaResourceType["PRACTITIONER_ROLE"] = "PractitionerRole";
    /**
     * FHIR - Procedure
     * CareSend - Procedure
     *
     * Health Gorilla implements FHIR Procedure as ProcedureRequest
     * and requires the `category` to be a `Diagnostic procedure`
     * https://www.hl7.org/fhir/procedure.html
     */
    HealthGorillaResourceType["PROCEDURE_REQUEST"] = "ProcedureRequest";
    /**
     * FHIR - Related Person
     * CareSend - EncryptedUser | User
     *
     * Information about a person that is involved in the care for a patient,
     * but who is not the target of healthcare,
     * nor has a formal responsibility in the care process.
     * For CareSend, this resource is generated primarily when creating Coverages
     * for a patient in Health Gorilla.
     * https://www.hl7.org/fhir/relatedperson.html
     */
    HealthGorillaResourceType["RELATED_PERSON"] = "RelatedPerson";
    /**
     * FHIR - Specimen
     * CareSend - Sample
     *
     * A sample to be used for analysis.
     * https://www.hl7.org/fhir/specimen.html
     */
    HealthGorillaResourceType["SPECIMEN"] = "Specimen";
})(HealthGorillaResourceType || (HealthGorillaResourceType = {}));
/**
 * The URLs corresponding to the different types of Organizations
 * Health Gorilla supports. These types are to be used when querying
 * Health Gorilla Organizations.
 *
 * ex: https://sandbox.healthgorilla.com/fhir/R4/Organization?type=HealthGorillaOrganizationType.INSURANCE_PROVIDER
 * Where HealthGorillaOrganizationType.INSURANCE_PROVIDER gets turned into a query param and encoded.
 */
export var HealthGorillaOrganizationType;
(function (HealthGorillaOrganizationType) {
    HealthGorillaOrganizationType["CAREQUALITY_PROVIDER"] = "https://www.healthgorilla.com/fhir/organization-type|CQ";
    HealthGorillaOrganizationType["INSURANCE_PROVIDER"] = "https://www.healthgorilla.com/fhir/organization-type|IP";
    HealthGorillaOrganizationType["FACILITY"] = "https://www.healthgorilla.com/fhir/organization-type|F";
    HealthGorillaOrganizationType["PRACTICE"] = "https://www.healthgorilla.com/fhir/organization-type|PR";
    HealthGorillaOrganizationType["PRACTICE_LOCATION"] = "https://www.healthgorilla.com/fhir/organization-type|PRL";
})(HealthGorillaOrganizationType || (HealthGorillaOrganizationType = {}));
/**
 * The URLs corresponding to the different types of Account.type.
 */
export var HealthGorillaOrderBillTo;
(function (HealthGorillaOrderBillTo) {
    HealthGorillaOrderBillTo["GUARANTOR"] = "guarantor";
    HealthGorillaOrderBillTo["PATIENT"] = "patient";
    HealthGorillaOrderBillTo["SELF"] = "self";
    HealthGorillaOrderBillTo["THIRD_PARTY"] = "thirdParty";
})(HealthGorillaOrderBillTo || (HealthGorillaOrderBillTo = {}));
/**
 * Identifiers used when making queries to Health Gorilla
 *
 * ex: https://sandbox.healthgorilla.com/fhir/R4/Practitioner?identifier=HealthGorillaIdentifier.NPI1528128915
 * Where HealthGorillaIdentifier.NPI will be encoded when added
 * as the query param with the identifier value as the complement.
 */
export var HealthGorillaIdentifier;
(function (HealthGorillaIdentifier) {
    HealthGorillaIdentifier["NPI"] = "http://hl7.org/fhir/sid/us-npi|";
})(HealthGorillaIdentifier || (HealthGorillaIdentifier = {}));
/**
 * Different systems when creating a new Coding resource for a resource type.
 *
 * ex: {
 *  resourceType: HealthGorillaResourceType.RELATED_PERSON,
 *  ...
 *  relationship: [
 *    {
 *      coding: [
 *        initCoding(HealthGorillaCodeSystem.SUBSCRIBER_RELATIONSHIP, relationship),
 *      ],
 *    },
 *  ],
 * }
 */
export var HealthGorillaCodeSystem;
(function (HealthGorillaCodeSystem) {
    HealthGorillaCodeSystem["ADMINISTRATIVE_ROLE"] = "https://www.healthgorilla.com/administrative-role";
    HealthGorillaCodeSystem["ORDER_BILL_TO"] = "https://www.healthgorilla.com/order-billto";
    HealthGorillaCodeSystem["COVERAGE_CLASS"] = "http://terminology.hl7.org/CodeSystem/coverage-class";
    HealthGorillaCodeSystem["POLICYHOLDER_RELATIONSHIP"] = "http://hl7.org/fhir/policyholder-relationship";
    HealthGorillaCodeSystem["PRACTITIONER_ROLE"] = "http://hl7.org/fhir/practitioner-role";
    HealthGorillaCodeSystem["SUBSCRIBER_RELATIONSHIP"] = "http://terminology.hl7.org/CodeSystem/subscriber-relationship";
    HealthGorillaCodeSystem["ORGANIZATION_TYPE"] = "http://terminology.hl7.org/CodeSystem/organization-type";
    HealthGorillaCodeSystem["US_NPI"] = "http://hl7.org/fhir/sid/us-npi";
    HealthGorillaCodeSystem["V2_0203"] = "http://terminology.hl7.org/CodeSystem/v2-0203";
})(HealthGorillaCodeSystem || (HealthGorillaCodeSystem = {}));
/**
 * Structure Definitions Health Gorilla made for different valuesets
 * to be used with defining Coding resources
 */
export var HealthGorillaStructureDefinition;
(function (HealthGorillaStructureDefinition) {
    HealthGorillaStructureDefinition["HG_ORDER_ACCOUNT"] = "https://healthgorilla.com/fhir/StructureDefinition/hg-order-account";
    HealthGorillaStructureDefinition["HG_PRACTITIONER"] = "https://healthgorilla.com/fhir/StructureDefinition/hg-practitioner";
    HealthGorillaStructureDefinition["PRACTITIONER_LOGIN"] = "https://www.healthgorilla.com/fhir/R4/StructureDefinition/hg-practitioner-login";
    HealthGorillaStructureDefinition["PRACTITIONER_PASSWORD"] = "https://www.healthgorilla.com/fhir/R4/StructureDefinition/hg-practitioner-password";
    HealthGorillaStructureDefinition["US_CORE_PRACTITIONER"] = "http://hl7.org/fhir/us/core/StructureDefinition/us-core-practitioner";
})(HealthGorillaStructureDefinition || (HealthGorillaStructureDefinition = {}));
/**
 * The endpoints used for API Calls to Health Gorilla
 * to perform different operations.
 */
export var HealthGorillaEndpoint;
(function (HealthGorillaEndpoint) {
    HealthGorillaEndpoint["OAUTH_TOKEN"] = "oauth/token";
    HealthGorillaEndpoint["COVERAGE"] = "fhir/R4/Coverage";
    HealthGorillaEndpoint["ORGANIZATION"] = "fhir/R4/Organization";
    HealthGorillaEndpoint["PATIENT"] = "fhir/R4/Patient";
    HealthGorillaEndpoint["PRACTITIONER"] = "fhir/R4/Practitioner";
    HealthGorillaEndpoint["PRACTITIONER_ROLE"] = "fhir/R4/PractitionerRole";
})(HealthGorillaEndpoint || (HealthGorillaEndpoint = {}));
//# sourceMappingURL=healthGorilla.js.map